// src/features/admin/pages/AdminProducts.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminProducts } from '../hooks/useAdminProducts';
import { CATEGORIES_DATA, GENDER_FILTERS, INTENSITY_FILTERS } from '../../october/constants/productConstants';
import { Edit, Trash2, Plus, Package, Search, Filter, X } from 'lucide-react';

const AdminProducts = () => {
  const navigate = useNavigate();
  const {
    products,
    loading,
    error,
    deleteProduct,
    toggleStock,
    toggleFeatured,
    searchProducts,
    refresh
  } = useAdminProducts();

  // Local state for filters and search
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }

    // Stock filter
    if (stockFilter === 'in-stock') {
      filtered = filtered.filter(product => product.inStock);
    } else if (stockFilter === 'out-of-stock') {
      filtered = filtered.filter(product => !product.inStock);
    }

    // Featured filter
    if (featuredFilter === 'featured') {
      filtered = filtered.filter(product => product.featured);
    } else if (featuredFilter === 'not-featured') {
      filtered = filtered.filter(product => !product.featured);
    }

    // Sort products
    switch (sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'price-low':
        filtered.sort((a, b) => a.priceValue - b.priceValue);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.priceValue - a.priceValue);
        break;
      case 'category':
        filtered.sort((a, b) => a.category.localeCompare(b.category));
        break;
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      default:
        break;
    }

    return filtered;
  }, [products, searchTerm, categoryFilter, stockFilter, featuredFilter, sortBy]);

  // Get category display info
  const getCategoryInfo = (categoryId) => {
    const category = CATEGORIES_DATA.find(cat => cat.id === categoryId);
    return category || { name: categoryId, icon: '📦' };
  };

  // Handle product selection
  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedProducts.length} products? This action cannot be undone.`)) {
      try {
        for (const productId of selectedProducts) {
          await deleteProduct(productId);
        }
        setSelectedProducts([]);
      } catch (error) {
        console.error('Bulk delete error:', error);
      }
    }
  };

  // Handle delete
  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (productToDelete) {
      try {
        await deleteProduct(productToDelete.id);
        setShowDeleteModal(false);
        setProductToDelete(null);
      } catch (error) {
        console.error('Delete error:', error);
      }
    }
  };

  // Handle stock toggle
  const handleStockToggle = async (productId) => {
    try {
      await toggleStock(productId);
    } catch (error) {
      console.error('Stock toggle error:', error);
    }
  };

  // Handle featured toggle
  const handleFeaturedToggle = async (productId) => {
    try {
      await toggleFeatured(productId);
    } catch (error) {
      console.error('Featured toggle error:', error);
    }
  };

  // Get category-specific details for display
  const getCategorySpecificDetails = (product) => {
  const detailStyle = "text-xs text-brand-light/60 font-body font-light";
    switch (product.category) {
      case 'perfumes':
        return (
          <div className={`${detailStyle} space-y-1`}>
            {product.intensity && <div>Intensity: <span className="text-brand-gold font-medium">{product.intensity}</span></div>}
            {product.longevity && <div>Longevity: <span className="text-brand-gold font-medium">{product.longevity}</span></div>}
            {product.gender && <div>Gender: <span className="text-brand-gold font-medium">{product.gender}</span></div>}
          </div>
        );
      case 'candles':
        return (
          <div className={`${detailStyle} space-y-1`}>
            {product.burnTime && <div>Burn Time: <span className="text-brand-gold font-medium">{product.burnTime}</span></div>}
            {product.roomSize && <div>Room Size: <span className="text-brand-gold font-medium">{product.roomSize}</span></div>}
            {product.wickType && <div>Wick: <span className="text-brand-gold font-medium">{product.wickType}</span></div>}
          </div>
        );
      case 'diffusers':
        return (
          <div className={`${detailStyle} space-y-1`}>
            {product.coverage && <div>Coverage: <span className="text-brand-gold font-medium">{product.coverage}</span></div>}
            {product.duration && <div>Duration: <span className="text-brand-gold font-medium">{product.duration}</span></div>}
            {product.capacity && <div>Capacity: <span className="text-brand-gold font-medium">{product.capacity}</span></div>}
          </div>
        );
      case 'room-mist':
        return (
          <div className={`${detailStyle} space-y-1`}>
            {product.coverage && <div>Coverage: <span className="text-brand-gold font-medium">{product.coverage}</span></div>}
            {product.duration && <div>Duration: <span className="text-brand-gold font-medium">{product.duration}</span></div>}
            {product.capacity && <div>Capacity: <span className="text-brand-gold font-medium">{product.capacity}</span></div>}
            {product.sprayType && <div>Spray Type: <span className="text-brand-gold font-medium">{product.sprayType}</span></div>}
          </div>
        );
      case 'bath':
        return (
          <div className={`${detailStyle} space-y-1`}>
            {product.size && <div>Size: <span className="text-brand-gold font-medium">{product.size}</span></div>}
            {product.scentProfile && <div>Scent: <span className="text-brand-gold font-medium">{product.scentProfile}</span></div>}
            {product.skinType && <div>Skin Type: <span className="text-brand-gold font-medium">{product.skinType}</span></div>}
          </div>
        );
      case 'others':
        return (
          <div className={`${detailStyle} space-y-1`}>
            {product.productType && <div>Type: <span className="text-brand-gold font-medium">{product.productType}</span></div>}
            {product.dimensions && <div>Dimensions: <span className="text-brand-gold font-medium">{product.dimensions}</span></div>}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-brand-black text-brand-light">
      {/* Header */}
      <div className="bg-brand-black/85 border-b border-brand-red/25 shadow-[0_20px_60px_-40px_rgba(230,0,0,0.6)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-0 sm:h-16 gap-4 sm:gap-0">
            <div className="flex items-center">
              <h1 className="text-2xl lg:text-3xl font-heading font-semibold text-brand-gold">Product Management</h1>
            </div>
              <button
                onClick={() => navigate('/admin/products/new')}
              className="flex items-center gap-2 px-6 py-3 bg-brand-red text-brand-light rounded-xl hover:bg-brand-red/80 transition-colors font-body font-medium touch-manipulation shadow-[0_20px_45px_-25px_rgba(230,0,0,0.7)]"
            >
              <Plus className="w-5 h-5" />
              <span>Add Product</span>
              </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        {/* Filters */}
        <div className="bg-brand-black/80 rounded-2xl border border-brand-red/20 p-4 sm:p-6 mb-6 shadow-[0_25px_60px_-35px_rgba(230,0,0,0.35)]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-body font-medium text-brand-light/80 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-brand-light/30" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light placeholder:text-brand-light/40 focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-base"
              />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-body font-medium text-brand-light/80 mb-2">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-base"
              >
                <option value="all">All Categories</option>
                {CATEGORIES_DATA.filter(cat => cat.id !== 'all' && !cat.coming).map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Stock Filter */}
            <div>
              <label className="block text-sm font-body font-medium text-brand-light/80 mb-2">Stock Status</label>
              <select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-base"
              >
                <option value="all">All Products</option>
                <option value="in-stock">In Stock</option>
                <option value="out-of-stock">Out of Stock</option>
              </select>
            </div>

            {/* Featured Filter */}
            <div>
              <label className="block text-sm font-body font-medium text-brand-light/80 mb-2">Featured</label>
              <select
                value={featuredFilter}
                onChange={(e) => setFeaturedFilter(e.target.value)}
                className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-base"
              >
                <option value="all">All Products</option>
                <option value="featured">Featured</option>
                <option value="not-featured">Not Featured</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-sm font-body font-medium text-brand-light/80 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-4 py-3 bg-brand-black border border-brand-red/30 rounded-xl text-brand-light focus:outline-none focus:ring-2 focus:ring-brand-gold/60 transition-all font-body text-base"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="category">Category</option>
                <option value="price-low">Price (Low to High)</option>
                <option value="price-high">Price (High to Low)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="text-sm font-body font-medium text-brand-light/70">
            <span className="text-brand-gold font-semibold">{filteredProducts.length}</span> of{' '}
            <span className="text-brand-gold font-semibold">{products.length}</span> products
            {categoryFilter !== 'all' && (
              <span className="ml-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-body font-medium bg-brand-red/20 text-brand-gold border border-brand-red/40">
                {getCategoryInfo(categoryFilter).icon} {getCategoryInfo(categoryFilter).name}
              </span>
            )}
          </div>
          {selectedProducts.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-body font-light text-brand-light/70">{selectedProducts.length} selected</span>
              <button 
                onClick={handleBulkDelete}
                className="px-4 py-2 text-sm bg-brand-red text-brand-light rounded-xl hover:bg-brand-red/80 transition-colors font-body font-medium touch-manipulation shadow-[0_20px_45px_-25px_rgba(230,0,0,0.6)]"
              >
                Delete Selected
              </button>
            </div>
          )}
        </div>

        {/* Products Table/Cards */}
        <div className="bg-brand-black/85 rounded-2xl border border-brand-red/20 overflow-hidden shadow-[0_30px_70px_-40px_rgba(230,0,0,0.45)]">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-brand-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-brand-light/60 font-body font-light">Loading products...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16 px-4">
              <p className="text-red-400 mb-4 font-body">{error}</p>
              <button
                onClick={refresh}
                className="px-6 py-3 bg-brand-red text-brand-light rounded-xl hover:bg-brand-red/80 transition-colors font-body font-medium"
              >
                Retry
              </button>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16 px-4">
              <Package className="h-16 w-16 text-brand-light/25 mx-auto mb-4" />
              <p className="text-brand-light/60 mb-6 font-body font-light">
                {searchTerm || categoryFilter !== 'all' || stockFilter !== 'all' || featuredFilter !== 'all' 
                  ? 'No products match your filters' 
                  : 'No products found'
                }
              </p>
              <button
                onClick={() => navigate('/admin/products/new')}
                className="px-6 py-3 bg-brand-red text-brand-light rounded-xl hover:bg-brand-red/80 transition-colors font-body font-medium"
              >
                Add Your First Product
              </button>
            </div>
          ) : isMobile ? (
            /* Mobile Card View */
            <div className="p-4 space-y-4">
              {filteredProducts.map((product) => {
                const categoryInfo = getCategoryInfo(product.category);
                return (
                  <div key={product.id} className="bg-brand-black/75 border border-brand-red/25 rounded-2xl p-4 shadow-[0_25px_60px_-35px_rgba(230,0,0,0.35)]">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start gap-3 flex-1">
                        {product.image && (
                          <img
                            src={product.image}
                            alt={product.name}
                            className="h-16 w-16 rounded-xl object-cover border border-brand-red/30 flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-heading font-medium text-brand-gold mb-1 truncate">{product.name}</h3>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-body font-medium bg-brand-red/20 text-brand-gold border border-brand-red/40">
                              {categoryInfo.icon} {categoryInfo.name}
                            </span>
                            {product.featured && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-body font-medium bg-brand-gold/20 text-brand-black border border-brand-gold/40">
                                Featured
                              </span>
                            )}
                          </div>
                          <p className="text-lg font-heading font-normal text-brand-light mb-2">{product.price}</p>
                        </div>
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedProducts.includes(product.id)}
                        onChange={() => handleSelectProduct(product.id)}
                        className="w-5 h-5 rounded border-brand-red/30 bg-brand-black text-brand-gold focus:ring-brand-gold mt-1 flex-shrink-0 touch-manipulation"
                      />
                    </div>

                    <div className="space-y-2 mb-4 text-sm font-body text-brand-light/70">
                      <div className="flex justify-between">
                        <span>Type:</span>
                        <span className="font-medium text-brand-light">{product.type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Stock:</span>
                        <button
                          onClick={() => handleStockToggle(product.id)}
                          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                            product.inStock
                              ? 'bg-brand-black/60 text-emerald-300 border border-emerald-500/40'
                              : 'bg-brand-black/60 text-red-400 border border-red-500/40'
                          }`}
                        >
                          {product.inStock ? 'In Stock' : 'Out of Stock'}
                        </button>
                      </div>
                      <div className="flex justify-between">
                        <span>Rating:</span>
                        <span className="font-medium text-brand-gold">{product.rating || 'N/A'} ⭐</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Created:</span>
                        <span className="font-medium text-brand-light/80">{new Date(product.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-brand-red/20">
                      <button
                        onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-red/20 text-brand-gold rounded-xl hover:bg-brand-red/30 transition-colors font-body font-medium touch-manipulation"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(product)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-black/60 text-red-400 border border-red-500/40 rounded-xl hover:bg-brand-black/40 transition-colors font-body font-medium touch-manipulation"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop Table View */
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-brand-light">
                <thead className="bg-brand-black border-b border-brand-red/20 text-xs uppercase tracking-wide text-brand-light/60">
                  <tr>
                    <th className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                        onChange={handleSelectAll}
                        className="w-5 h-5 rounded border-brand-red/30 bg-brand-black text-brand-gold focus:ring-brand-gold"
                      />
                    </th>
                    <th className="px-6 py-4">
                      Product
                    </th>
                    <th className="px-6 py-4">
                      Category & Type
                    </th>
                    <th className="px-6 py-4">
                      Details
                    </th>
                    <th className="px-6 py-4">
                      Price
                    </th>
                    <th className="px-6 py-4">
                      Stock
                    </th>
                    <th className="px-6 py-4">
                      Featured
                    </th>
                    <th className="px-6 py-4">
                      Created
                    </th>
                    <th className="px-6 py-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-red/15 text-sm">
                  {filteredProducts.map((product) => {
                    const categoryInfo = getCategoryInfo(product.category);
                    
                    return (
                      <tr key={product.id} className="hover:bg-brand-black/70 transition-colors">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedProducts.includes(product.id)}
                            onChange={() => handleSelectProduct(product.id)}
                            className="w-5 h-5 rounded border-brand-red/30 bg-brand-black text-brand-gold focus:ring-brand-gold"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            {product.image && (
                              <img
                                src={product.image}
                                alt={product.name}
                            className="h-12 w-12 rounded-xl object-cover mr-3 border border-brand-red/30"
                              />
                            )}
                            <div>
                              <div className="text-sm font-body font-medium text-brand-gold">{product.name}</div>
                              <div className="text-xs text-brand-light/60 font-body font-light">
                                Rating: {product.rating || 'N/A'} ⭐
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-body font-medium bg-brand-red/20 text-brand-gold border border-brand-red/40 mb-1">
                              {categoryInfo.icon} {categoryInfo.name}
                            </span>
                            <div className="text-sm text-brand-light/70 font-body font-light">{product.type}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getCategorySpecificDetails(product)}
                        </td>
                        <td className="px-6 py-4 text-sm font-heading font-normal text-brand-light">
                          {product.price}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleStockToggle(product.id)}
                            className={`inline-flex px-3 py-1 text-xs font-body font-medium rounded-full transition-colors ${
                              product.inStock
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30'
                                : 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                            }`}
                          >
                            {product.inStock ? 'In Stock' : 'Out of Stock'}
                          </button>
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleFeaturedToggle(product.id)}
                            className={`inline-flex px-3 py-1 text-xs font-body font-medium rounded-full transition-colors ${
                              product.featured
                                ? 'bg-brand-red/20 text-brand-gold border border-brand-red/40 hover:bg-brand-red/30'
                                : 'bg-brand-black/60 text-brand-light/70 border border-brand-red/30 hover:bg-brand-black/40'
                            }`}
                          >
                            {product.featured ? 'Featured' : 'Regular'}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-brand-light/60 font-body font-light">
                          {new Date(product.createdAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                              className="p-2 text-brand-light hover:bg-brand-red/20 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(product)}
                              className="p-2 text-red-400 hover:bg-brand-black/60 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-brand-black/85 backdrop-blur-xl flex items-center justify-center z-50 p-4">
          <div className="relative bg-brand-black border border-red-400/40 rounded-3xl p-6 max-w-md w-full shadow-[0_30px_80px_-35px_rgba(230,0,0,0.6)]">
            <h3 className="text-xl font-heading font-semibold text-brand-gold mb-4">Delete Product</h3>
            <p className="text-brand-light/70 font-body font-light mb-6">
              Are you sure you want to delete "<span className="font-medium text-brand-gold">{productToDelete?.name}</span>"? This action cannot be undone.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-6 py-3 border border-brand-red/30 text-brand-light rounded-xl hover:bg-brand-black/70 transition-colors font-body font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 px-6 py-3 bg-brand-red text-brand-light rounded-xl hover:bg-brand-red/80 transition-colors font-body font-medium shadow-[0_25px_60px_-30px_rgba(230,0,0,0.6)]"
              >
                Delete
              </button>
              </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;